/**
 * MULTI-ORG P4 → STRICT (2026-08-18 owner directive: "each thread needs to
 * be totally contained within an organization for its life, only seen when
 * the user's portal is in that organization — complete separation and clear
 * distinction"): the per-workspace thread filter — THE separation rule every
 * surface inherits (leaf module: pure, no firebase imports, unit-testable in
 * isolation).
 *
 * A thread is visible in the active workspace ONLY when its binding matches
 * the active workspace. An UNBOUND thread (create raced the resolution, or
 * pre-backfill legacy) is hidden from every resolved workspace — never
 * rendered cross-org. It is not lost: creates now PARK until the workspace
 * resolves and flush as BOUND full-shape writes (chatThreadIndexRepository
 * deferred creates — bind-at-birth), and the M4 sweep (multiOrgBindingBackfill,
 * hourly, canonical-aware) binds any legacy stock — the thread reappears
 * inside its own organization.
 * (The original P4 fail-open showed unbound threads EVERYWHERE, which the
 * owner observed as organizations sharing one thread list.)
 *
 * No resolved workspace yet (boot, guests) → unfiltered (fail-open on a
 * VIEW; money/authz separation is server-enforced regardless).
 *
 * HOME-WORKSPACE EXCEPTION (owner report 2026-08-19: "threads are
 * disappearing when a user comes back"): hiding unbound threads from EVERY
 * workspace made them vanish entirely for up to 24h (the M4 sweep's re-arm
 * window) — or forever when the sweep bound them to the CANONICAL org of a
 * multi-workspace member instead of the org they were created in. An
 * unbound thread now surfaces in exactly ONE place: the user's own HOME
 * workspace (their uid-keyed personal account), where the existing
 * back-stamp binds it on first open/send. Org workspaces stay strictly
 * separated — the original leak (threads shared across organizations)
 * cannot recur, because home is never another organization.
 */
import type { ChatThread } from './types';

export function filterThreadsForWorkspace(
  threads: ChatThread[],
  activeAccountId?: string | null,
  opts?: { homeAccountId?: string | null },
): ChatThread[] {
  const active = (activeAccountId || '').trim();
  if (!active) return threads;
  const home = (opts?.homeAccountId || '').trim();
  const activeIsHome = Boolean(home) && active === home;
  return threads.filter((t) => t.accountId === active || (activeIsHome && !t.accountId));
}

/**
 * WORKSPACE CONTAINMENT for a single thread (owner directive 2026-08-19:
 * "ensure they are entirely separated views even security etc." — a Prest0
 * Inc. thread stayed OPEN in the Miles Maggio workspace's pane after a
 * switch, while the rail correctly showed none). The rail filter above
 * contains ROWS; this is the same rule for the ACTIVE thread and every open
 * path: a thread may render inside a resolved workspace only when it belongs
 * there. No workspace resolved yet → allowed (boot window; the containment
 * effect re-checks the moment resolution lands).
 */
export function threadAllowedInWorkspace(
  thread: Pick<ChatThread, 'accountId'> | null | undefined,
  activeAccountId?: string | null,
  homeAccountId?: string | null,
): boolean {
  if (!thread) return false;
  const active = (activeAccountId || '').trim();
  if (!active) return true;
  const bound = (thread.accountId || '').trim();
  if (bound) return bound === active;
  const home = (homeAccountId || '').trim();
  return Boolean(home) && active === home;
}
